import React, { useContext, useMemo } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useGroup } from "../../api/useQueries";
import { LegacyLoader } from "../../../../../../components/Loader/Loader";
import { useUpdateGroup } from "../../api/useMutations";
import { toast } from "react-toastify";
import { defineMessages, useIntl } from "react-intl";
import { useQueryClient } from "@tanstack/react-query";
import queryKeys from "../../api/queryKeys";
import { sessionStore } from "../../../../../../contexts/SessionContext";

import { Modal } from "@evidenceb/athena-common/design-system/Modal";
import { Icon } from "@evidenceb/athena-common/design-system/Icon";
import { useOverlayTriggerState } from "react-stately";
import {
    TreeNode,
    nodePathsToUniqueLeafId,
} from "../../../../../../components/TreeCheckboxGroup";
import { prepareClassroomsForTreeCheckboxGroup } from "../../../../../Workmodes/Playlist/Teacher/EditPlaylist/utils";
import { useDashboardRouter } from "../../../routing";
import DashBoardGroupForm from "../DashboardGroupForm/DashboardGroupForm";
import { GroupFormValues } from "../../../../../../components/GroupForm/GroupForm";

const EditGroup = () => {
    const intl = useIntl();
    const {
        session: { userId, classrooms },
    } = useContext(sessionStore);
    const history = useHistory();
    const { classroomList: classroomListUrl } = useDashboardRouter();

    const students = useMemo<Record<string, TreeNode>>(
        () => prepareClassroomsForTreeCheckboxGroup(classrooms),
        [classrooms]
    );

    const groupEditionErrorModal = useOverlayTriggerState({});

    const { groupId } = useParams<{ groupId: string }>();
    const { data: group } = useGroup(groupId);

    const queryClient = useQueryClient();
    const { mutateAsync: updateGroup, isLoading: isSubmitting } =
        useUpdateGroup();

    if (!group || isSubmitting) return <LegacyLoader />;

    const onSubmit = (values: GroupFormValues) => {
        updateGroup(
            {
                ...group,
                name: values.name,
                students: nodePathsToUniqueLeafId(values.students, students),
            },
            {
                onSuccess: () => {
                    toast(
                        intl.formatMessage(messages.groupEditionSuccessToast),
                        {
                            containerId: "action-feedback",
                            icon: <Icon path="checkmark" />,
                        }
                    );
                    queryClient.invalidateQueries(queryKeys.getGroup(group.id));
                    queryClient.invalidateQueries(queryKeys.getGroups(userId));
                    history.push(classroomListUrl);
                },
                onError: () => {
                    groupEditionErrorModal.open();
                },
            }
        );
    };

    return (
        <>
            <DashBoardGroupForm
                defaultValues={{
                    name: group.name,
                    students: group.students.map((student) => student.id),
                }}
                onSubmit={onSubmit}
                students={students}
                goBackUrl={classroomListUrl}
            />
            {groupEditionErrorModal.isOpen && (
                <Modal
                    footerButtons={[
                        {
                            label: intl.formatMessage(
                                messages.groupEditionErrorDismissButton
                            ),
                            variant: "custom",
                            colors: {
                                background: "var(--element-teacher-default)",
                                hoverBackground:
                                    "var(--element-button-primary-hover)",
                                text: "var(--element-button-primary-text-hover)",
                            },
                            onPress: () => groupEditionErrorModal.close(),
                        },
                    ]}
                    title={intl.formatMessage(messages.groupEditionErrorTitle)}
                    open={groupEditionErrorModal.isOpen}
                    onClose={() => groupEditionErrorModal.close()}
                >
                    <div className="create-group__error-modal__content">
                        {intl.formatMessage(
                            messages.groupEditionErrorDescription
                        )}
                    </div>
                </Modal>
            )}
        </>
    );
};

export default EditGroup;

const messages = defineMessages({
    groupEditionSuccessToast: {
        id: "edit-group-successToast",
        defaultMessage: "Your group was successfully updated",
    },
    groupEditionErrorTitle: {
        id: "edit-group-error-title",
        defaultMessage: "An error occured while editing your group",
    },
    groupEditionErrorDescription: {
        id: "edit-group-error-description",
        defaultMessage:
            "We are having difficulty editing your group. We are doing our best to solve the issue quickly. We suggest you try again later.",
    },
    groupEditionErrorDismissButton: {
        id: "edit-group-error-dismissButton",
        defaultMessage: "Ok",
    },
});
